import { Upload } from '@aws-sdk/lib-storage';
import { GetObjectCommandInput, PutObjectCommandInput, S3 as AWSS3, ObjectCannedACL } from '@aws-sdk/client-s3';
import { Readable } from 'stream';
import { v4 as uuid } from 'uuid';
import { Request } from 'express';

export type NotificationEvent = {
	Records: {
		eventVersion: string;
		eventSource: 'aws:s3';
		awsRegion: string;
		eventTime: string;
		userIdentity: {
			principalId: string;
		};
		requestParameters: {
			sourceIPAddress: string;
		};
		responseElements: {
			'x-amz-request-id': string;
			'x-amz-id-2': string;
		};
		s3: {
			s3SchemaVersion: string;
			configurationId: string;
			bucket: {
				name: string;
				ownerIdentity: {
					principalId: string;
				};
				arn: string;
			};
			object: {
				key: string;
				size: number;
				eTag: string;
				sequencer: string;
			};
		};
	}[];
};

export class S3 {
	private static readonly isDebug = process.env.DEBUG || false;

	private static s3Config = {
		region: process.env.AWS_S3_REGION || process.env.AWS_REGION || '',
		bucketName: process.env.AWS_S3_BUCKET_NAME,
		accessKeyId: process.env.AWS_S3_ACCESS_KEY_ID || process.env.AWS_ACCESS_KEY_ID,
		secretAccessKey: process.env.AWS_S3_SECRET_ACCESS_KEY || process.env.AWS_SECRET_ACCESS_KEY
	};

	public static async upload(
		fileBuffer: Buffer,
		fileName: string,
		fileMime: string,
		folderName?: string,
		generateNewName?: boolean,
		acl: ObjectCannedACL = ObjectCannedACL.public_read
	) {
		const s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey
			}
		});

		if (!folderName) {
			folderName = 'uploads/';
		} else if (!folderName.match(/\/$/)) {
			folderName = `${folderName}/`;
		}

		if (generateNewName) {
			fileName = this.generateFileName(fileName);
		}

		console.log('/////////////////S3DEBUG/////////////////');
		console.log('::S3DEBUG:: Bucket Name: ', this.s3Config.bucketName);
		console.log('::S3DEBUG:: Folder Name: ', folderName);
		console.log('::S3DEBUG:: File Name: ', fileName);
		console.log('::S3DEBUG:: ACL: ', acl);
		console.log('::S3DEBUG:: Region: ', this.s3Config.region);
		console.log('/////////////////S3DEBUG/////////////////');

		const file: PutObjectCommandInput = {
			ACL: acl,
			Bucket: this.s3Config.bucketName,
			Key: folderName + fileName,
			Body: fileBuffer,
			ContentType: fileMime
		};

		const response = await s3
			.putObject(file)
			.catch(err => {
				if (this.isDebug) {
					console.log(err);
				}
				return false;
			});

		s3.destroy();

		if (response) {
			if (this.isDebug) {
				console.log('File uploaded to S3.');
			}
			return {
				status: 'success',
				message: 'File uploaded to s3.',
				path: 'https://' + this.s3Config.bucketName + `.s3${this.s3Config.region?.length ? '.' + this.s3Config.region : ''}.amazonaws.com/` + folderName + fileName
			};
		} else {
			throw 'Couldn\'t write file to s3...';
		}
	}

	public static async uploadStream(
		fileBuffer: Readable,
		fileName: string,
		fileMime: string,
		folderName?: string,
		generateNewName?: boolean
	) {
		const s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey
			}
		});

		if (!folderName) {
			folderName = 'uploads/';
		} else if (!folderName.match(/\/$/)) {
			folderName = `${folderName}/`;
		}

		if (generateNewName) {
			fileName = this.generateFileName(fileName);
		}

		let file: PutObjectCommandInput = {
			Bucket: this.s3Config.bucketName,
			Key: folderName + fileName,
			Body: fileBuffer,
			ContentType: fileMime,
			ACL: ObjectCannedACL.public_read
		};

		let response = await new Upload({
			client: s3,
			params: file
		})
		.done()
		.catch(err => {
			console.log(err);
			return false;
		});

		s3.destroy();

		if (response) {
			console.log('File uploaded to S3.');
			return {
				status: 'succeeded',
				message: 'File uploaded to s3.',
				path: 'https://' + this.s3Config.bucketName + `.s3${this.s3Config.region?.length ? '.' + this.s3Config.region : ''}.amazonaws.com/` + folderName + fileName
			};
		} else {
			throw 'Couldn\'t write file to s3...';
		}
	}

	public static async getFileStream(fileKey: string, req?: Request): Promise<{ stream: Readable; contentType: string }> {
		const params: GetObjectCommandInput = {
			Bucket: this.s3Config.bucketName,
			Key: fileKey
		};

		// In AWS SDK v3, httpOptions are set via the requestHandler. 
		// Use NodeHttpHandler to set timeouts.
		const timeoutInMs = 20 * 60 * 1000;
		const s3 = new AWSS3([{
			requestHandler: {
				connectionTimeout: timeoutInMs,
				socketTimeout: timeoutInMs
			},
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey
			}
		}]);

		const headObjectResponse = await s3
			.headObject(params)
			.catch(err => {
				console.log(err);
				return null;
			});

		s3.destroy();

		if (!headObjectResponse) {
			throw new Error(`Couldn't get file headers.`);
		}

		const contentType = headObjectResponse.ContentType || 'application/octet-stream';

		if (req?.headers?.range) {
			params.Range = req.headers.range;
		}

		const getObjectResponse = await s3.getObject(params);
		const stream = getObjectResponse.Body as Readable;

		return { stream, contentType };
	}

	public static async remove(objects) {

		let params = {
			Bucket: this.s3Config.bucketName,
			Delete: {
				Objects: objects
			}
		};

		let s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey
			}
		});

		let response = await s3
			.deleteObjects(params)
			.catch(err => {
				console.log(err);
				return false;
			});

		s3.destroy();

		if (response) {
			console.log('File(s) removed from S3.');
			return response;
		} else {
			throw 'Error removing file(s)...';
		}
	}

	public static async getContents(folderName: string, recursive: boolean = false, continuationToken?: string, progress: any[] = []) {
		if (!folderName) {
			folderName = 'uploads/';
		}

		let params: any = {
			Bucket: this.s3Config.bucketName,
			Prefix: folderName
		};

		if (continuationToken) {
			params.ContinuationToken = continuationToken;
		}

		let s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey
			}
		});

		let response = await s3
			.listObjectsV2(params)
			.catch(err => {
				console.log(err);
				return null;
			});

		s3.destroy();

		if (response) {
			if (recursive) {
				if (!response.Contents) {
					response.Contents = [];
				}
				if (response.NextContinuationToken) {
					return this.getContents(folderName, true, response.NextContinuationToken, [...response.Contents, ...progress]);
				} else {
					response.Contents = [...response.Contents, ...progress];
					return response;
				}
			} else {
				return response;
			}
		} else {
			throw new Error('Error listing bucket contents...');
		}
	}

	private static generateFileName(originalName) {
		let segments = originalName.match(/(.*)\.(.*)$/);

		return uuid() + '.' + segments[2];
	}
}
